/**
 * This component improves Mui's TreeView component to be able to process Layers data.
 */
import { useEffect, useState } from 'react';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { logger } from '@/core/utils/logger';
import type { TypeGeoviewLayerConfig, TypeLayerEntryConfig } from '@/api/types/layer-schema-types';
import { UtilAddLayer } from '@/core/components/layers/left-panel/add-new-layer/add-layer-utils';
import { ConfigBaseClass } from '@/api/config/validation-classes/config-base-class';
import { Tooltip } from '@/ui/tooltip/tooltip';

export interface AddLayerTreeProps {
  layerTree: TypeGeoviewLayerConfig;
  onSelectedItemsChange(items: string[]): void;
}

export function AddLayerTree(props: AddLayerTreeProps): JSX.Element | null {
  // Log
  logger.logTraceRender('components/layers/left-panel/add-layer-tree/add-layer-tree');

  const { layerTree, onSelectedItemsChange } = props;
  const [selectedItems, setSelectedItems] = useState<string[]>([]); // e.g. ["group1/layer1", "group2/layer2"]

  useEffect(() => {
    // Log
    logger.logTraceUseEffect('Add Layer Tree - selectedItems ', selectedItems);
    onSelectedItemsChange(selectedItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems]);

  /**
   * Recursive function to render tree item. It renders the layer and its children.
   * @param layer - the layer to render
   * @param parentId - the parent id of the layer
   */
  const renderTreeItem = (layer: TypeGeoviewLayerConfig | TypeLayerEntryConfig, parentId?: string): JSX.Element => {
    // Depending on the instance
    let layerId: string | undefined;
    let layerName: string | undefined;
    if (layer instanceof ConfigBaseClass) {
      ({ layerId } = layer);
      layerName = layer.getLayerName();
    } else {
      layerId = layer.geoviewLayerId;
      layerName = layer.geoviewLayerName;
    }

    const curLayerId = `${parentId ? `${parentId}/` : ''}${layerId}`;
    return (
      <Tooltip title={layerName} placement="top">
        <TreeItem key={curLayerId} itemId={curLayerId} label={layerName} aria-label={layerName}>
          {layer.listOfLayerEntryConfig?.map((subLayer) => renderTreeItem(subLayer, curLayerId))}
        </TreeItem>
      </Tooltip>
    );
  };

  /**
   * Get all children of a layer
   * @param treeLayerId - the id of the layer
   * @returns - the list of children of the layer
   */
  const getLayerChildren = (treeLayerId: string): string[] => {
    const result: string[] = [];

    function populateLayerChildren(origLayerId: string, parentViewId: string | undefined): void {
      const viewLayerId = `${parentViewId ?? ''}${parentViewId ? '/' : ''}${origLayerId}`;
      result.push(viewLayerId);
      const layerDetails = UtilAddLayer.getLayerById(layerTree, origLayerId);

      const childLayerIds: string[] | undefined = layerDetails?.listOfLayerEntryConfig?.map((child) => {
        return child.layerId;
      });

      childLayerIds?.forEach((childLayerId) => {
        populateLayerChildren(childLayerId, viewLayerId);
      });
    }

    const layerTokens = treeLayerId.split('/');
    const origLayerId = layerTokens.pop();
    let parentLayerId;
    if (layerTokens.length > 0) {
      parentLayerId = layerTokens.join('/');
    }
    if (origLayerId) populateLayerChildren(origLayerId, parentLayerId);

    return [...new Set(result)].sort();
  };

  const handleItemSelectionToggle = (event: React.SyntheticEvent, itemId: string, isSelected: boolean): void => {
    const layerChildren = getLayerChildren(itemId);
    const toAddOrRemove = [itemId, ...layerChildren];
    const splitId = itemId.split('/');
    splitId.pop();
    const parentId = splitId.join('/');

    if (isSelected) {
      setSelectedItems([...new Set([...selectedItems, ...toAddOrRemove])].sort());
    } else if (parentId && !selectedItems.find((selectedItem) => selectedItem.startsWith(`${parentId}/`) && selectedItem !== itemId))
      setSelectedItems(selectedItems.filter((item) => item !== parentId && item !== itemId));
    else setSelectedItems(selectedItems.filter((item) => !toAddOrRemove.includes(item)));
  };

  const renderTreeItems = (): JSX.Element[] => {
    // If the layer tree is a TypeGeoviewLayerConfig of type EsriDynamic or WFS
    if (
      layerTree.geoviewLayerType === 'esriDynamic' ||
      layerTree.geoviewLayerType === 'ogcWfs' ||
      layerTree.geoviewLayerType === 'ogcWms' ||
      layerTree.geoviewLayerType === 'ogcFeature'
    ) {
      return layerTree.listOfLayerEntryConfig.map((layer) => renderTreeItem(layer));
    }
    return [renderTreeItem(layerTree)];
  };

  return (
    <SimpleTreeView
      sx={{
        fontSize: '0.8rem',
        '& .MuiTreeItem-label': {
          fontSize: '0.8rem !important',
          paddingTop: '3px',
          paddingBottom: '3px',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        },
      }}
      multiSelect
      checkboxSelection
      selectedItems={selectedItems}
      onItemSelectionToggle={(event: React.SyntheticEvent, itemId: string, isSelected: boolean) =>
        handleItemSelectionToggle(event, itemId, isSelected)
      }
    >
      {renderTreeItems()}
    </SimpleTreeView>
  );
}
